import { useCallback } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Navigates to the auth page while ensuring there is a
 * redirect parameter is set to the current location.
 *
 * This is useful for when we want to redirect the user to the auth page
 * after they have signed in or signed up.
 */
const useNavigateToAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Sets the redirect parameter to the current location.
   */
  const setAuthRedirection = useCallback(
    ({
      redirect,
      additionalParams,
    }: {
      redirect?: string;
      additionalParams?: Record<string, string>;
    }) => {
      const searchParams = new URLSearchParams();
      searchParams.set('redirect', redirect ?? location.pathname);
      if (additionalParams) {
        Object.entries(additionalParams).forEach(([key, value]) => {
          searchParams.set(key, value);
        });
      }
      return searchParams;
    },
    [location.pathname]
  );

  /**
   * Navigates to the auth page while setting the
   * redirect parameter to the current location.
   *
   * Optionally, a redirect parameter can be provided to redirect
   * to a specific page after signing in or signing up.
   *
   * Optionally, additional parameters can be provided to add to the search params.
   */
  const handleNavigateToAuth = useCallback(
    ({
      page,
      redirect,
      additionalParams,
    }: {
      page: 'sign-in' | 'sign-up';
      redirect?: string;
      additionalParams?: Record<string, string>;
    }) => {
      const searchParams = setAuthRedirection({ redirect, additionalParams });
      navigate(`/${page}?${searchParams.toString()}`);
    },
    [navigate, setAuthRedirection]
  );

  return {
    handleNavigateToAuth,
    setAuthRedirection,
  };
};

export { useNavigateToAuth };
